import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { RelatedProductsComponentDef } from '@df/catalog-ui/related-products/related-products.component.def';
import { Attribute } from '@df/catalog/attribute/attribute';
import { IProductData, IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { BasketService, IAddToBasketEventHandle, IAddToBasketRequestData } from '@df/session/api/basket/basket.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsModalService } from '@icms/client/assets/modal/icms-modal.service';
import each from 'lodash-es/each';
import groupBy from 'lodash-es/groupBy';
import sortBy from 'lodash-es/sortBy';
import { NProductViewLayout, Product } from '../product.decorator';
import { NProductViewUpsellProducts } from './product-view-upsell-products.namespace';
@Component({
    selector: 'product-view-upsell-products',
    templateUrl: '/product-view-upsell-products.html',
    styleUrls: ['/product-view-upsell-products.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [PriceComponent, CommonModule, TranslationModule]
})
export class ProductViewUpsellProductsComponent extends RelatedProductsComponentDef {
    override limit = Infinity;
    selection: number[] = [];
    selectionProducts: Product<IProductData>[] = [];
    @Input()
    content?: NProductViewLayout.IBlockDataContent;
    override options = { engines: 'upsell', normalize: false, nonRoutable: true };
    groups: NProductViewUpsellProducts.IUpsellGroup[] = [];
    private app = inject(App);
    private cmsModalService = inject(ICmsModalService);
    private basketService = inject(BasketService);
    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(
            this.basketService.addToBasketBeforeSubject.subscribe(
                (event: { requestData: IAddToBasketRequestData; handle: IAddToBasketEventHandle }) =>
                    this.onAddToBasketBefore(event?.requestData)
            )
        );
    }
    get attribute(): Attribute | undefined {
        return this.app.resources.attribute.getExisting(NProductViewUpsellProducts.GROUP_BY_ATTRIBUTE_CODE);
    }
    private onAddToBasketBefore(requestData: IAddToBasketRequestData): void {
        if (requestData?.products && this.selection) {
            each(this.selection, value => {
                requestData.products.push({ qty: 1, id: value });
            });
        }
    }
    override onModelsChange(): void {
        super.onModelsChange();
        this.updateUpsells();
    }
    private updateUpsells(): void {
        this.groups = this.getGroups();
        this.selection = [];
        this.detectChanges();
    }

    trackByGroupId(index: number, group: NProductViewUpsellProducts.IUpsellGroup): string {
        return group.id;
    }

    trackByProductId(index: number, product: Product): number {
        return product.id;
    }

    openModal(): void {
        const label = this.label;
        if (this.content) {
            const modalData: NProductViewUpsellProducts.IModalData = {
                groups: this.groups,
                onConfirm: data => {
                    this.selectionProducts = sortBy(
                        data.selection.map(i => this.app.resources.product.get(i)) as Product<IProductData>[],
                        p => p.data?.[NProductViewUpsellProducts.GROUP_BY_ATTRIBUTE_CODE]
                    );
                    this.selection = this.selectionProducts.map(i => i.id);
                    // replace upsells in groups to ones sellected in modal
                    if (data.replacements) {
                        Object.entries(data.replacements).forEach(([key, value], i) => {
                            this.groups.forEach(group => {
                                const productIndex = group.products.findIndex(product => product.id === parseInt(key, 10));
                                productIndex !== -1 &&
                                    (group.products[productIndex] = this.app.resources.product.get(value) as Product<IProductDataV3>);
                            });
                        });
                    }
                    this.detectChanges();
                },
                label,
                selection: this.selection,
                content: this.content
            };
            this.cmsModalService.open(undefined, NProductViewUpsellProducts.MODAL_ID, label, modalData);
        }
    }

    private getGroups(): NProductViewUpsellProducts.IUpsellGroup[] {
        const products = (this.listing?.models || []).filter(
            i => i.isSalable() && i.data?.[NProductViewUpsellProducts.GROUP_BY_ATTRIBUTE_CODE]
        );
        if (products.length && this.attribute) {
            const grouped = groupBy(products, i => i.data?.[NProductViewUpsellProducts.GROUP_BY_ATTRIBUTE_CODE]);
            const groups: NProductViewUpsellProducts.IUpsellGroup[] = [];
            each(grouped, (value, key) => {
                const keyNumber = parseInt(key);
                const label = this.attribute?.getOptionLabel(keyNumber || key)?.toString();
                groups.push({
                    id: key,
                    label,
                    products: value as Product<IProductDataV3>[]
                });
            });
            return groups;
        } else {
            return [];
        }
    }

    get label(): string {
        return this.groups.map(i => i.label).join(' & ');
    }
}
